import PortalIdParser from 'PortalIdParser';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import { normalizeObjectType } from 'copilot-toolkit/utils/parserUtils';
import { CompaniesIcon } from 'foundations-assets/icons/components/CompaniesIcon';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { CustomObjectIcon } from 'foundations-assets/icons/components/CustomObjectIcon';
import { DealsIcon } from 'foundations-assets/icons/components/DealsIcon';
import { TicketIcon } from 'foundations-assets/icons/components/TicketIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import styled from 'foundations-theming-specialty/utils';
import { getFullUrlWithCurrentSubDomain } from 'hubspot-url-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CrmRecordChipCard } from './CrmRecordChipCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AT_MENTION_ICON_COMPONENTS = {
  CONTACT: ContactsIcon,
  COMPANY: CompaniesIcon,
  DEAL: DealsIcon,
  TICKET: TicketIcon,
  CUSTOM_OBJECT: CustomObjectIcon
};
export const HIDE_DISPLAY_NAME_PROPERTIES = {
  CONTACT: ['firstname', 'lastname'],
  COMPANY: ['name'],
  DEAL: ['dealname'],
  TICKET: ['subject']
};

// Overrides Canvas-theme behavior for links having an underline
// Props available to override this do not work and are deprecated
const RemoveTextDecorationForCanvasLinkWrapper = styled.span.withConfig({
  displayName: "CrmRecordChip__RemoveTextDecorationForCanvasLinkWrapper"
})(["a,a:hover,a:focus,a:active{text-decoration:none !important;text-decoration-line:none !important;}"]);
const CrmRecordChip = ({
  objectId,
  objectTypeId,
  name,
  displayName,
  urls,
  primaryProperty,
  secondaryProperties = [],
  icon,
  disabledUrlLinking = false,
  onViewImpression,
  onChipClick,
  onPopoverShown,
  onPopoverLinkClick
}) => {
  var _urls$find$url, _urls$find, _AT_MENTION_ICON_COMP;
  const resolvedDisplayName = displayName || name || '';
  const url = (_urls$find$url = urls === null || urls === void 0 || (_urls$find = urls.find(u => u.clientSurface === 'EMBEDDED')) === null || _urls$find === void 0 ? void 0 : _urls$find.url) !== null && _urls$find$url !== void 0 ? _urls$find$url : `${getFullUrlWithCurrentSubDomain()}/contacts/${PortalIdParser.get()}/record/${objectTypeId}/${objectId}`;
  const linkHref = disabledUrlLinking ? undefined : url;
  const normalizedObjectType = normalizeObjectType(objectTypeId);
  /* @deprecated Use icon instead */
  const IconComponent = (_AT_MENTION_ICON_COMP = AT_MENTION_ICON_COMPONENTS[normalizedObjectType]) !== null && _AT_MENTION_ICON_COMP !== void 0 ? _AT_MENTION_ICON_COMP : AT_MENTION_ICON_COMPONENTS.CUSTOM_OBJECT;
  const PopoverIconComponent = icon || IconComponent;
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const closeDelayTimeoutRef = useRef(null);
  const openDelayTimeoutRef = useRef(null);
  const hasTrackedImpressionRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedImpressionRef.current) {
      hasTrackedImpressionRef.current = true;
      onViewImpression === null || onViewImpression === void 0 || onViewImpression(objectTypeId, objectId);
    }
  }, [objectTypeId, objectId, onViewImpression]);
  const clearCloseDelayTimeout = useCallback(() => {
    if (closeDelayTimeoutRef.current) {
      clearTimeout(closeDelayTimeoutRef.current);
      closeDelayTimeoutRef.current = null;
    }
  }, []);
  const clearOpenDelayTimeout = useCallback(() => {
    if (openDelayTimeoutRef.current) {
      clearTimeout(openDelayTimeoutRef.current);
      openDelayTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => {
    return () => {
      clearCloseDelayTimeout();
      clearOpenDelayTimeout();
    };
  }, [clearCloseDelayTimeout, clearOpenDelayTimeout]);
  const handleChipMouseEnter = useCallback(() => {
    if (isSticky) return;
    clearCloseDelayTimeout();
    clearOpenDelayTimeout();
    openDelayTimeoutRef.current = setTimeout(() => {
      setInternalOpen(true);
      onPopoverShown === null || onPopoverShown === void 0 || onPopoverShown(objectTypeId);
    }, BREEZE_TOOLTIP_DELAY);
  }, [isSticky, clearCloseDelayTimeout, clearOpenDelayTimeout, objectTypeId, onPopoverShown]);
  const handleChipMouseLeave = useCallback(() => {
    if (isSticky) return;
    clearOpenDelayTimeout();
    closeDelayTimeoutRef.current = setTimeout(() => {
      setInternalOpen(false);
    }, 300);
  }, [isSticky, clearOpenDelayTimeout]);
  const handlePopoverMouseEnter = useCallback(() => {
    if (isSticky) return;
    clearCloseDelayTimeout();
  }, [isSticky, clearCloseDelayTimeout]);
  const handlePopoverMouseLeave = useCallback(() => {
    if (isSticky) return;
    setInternalOpen(false);
  }, [isSticky]);
  const handlePopoverInteraction = useCallback(() => {
    setIsSticky(true);
    clearCloseDelayTimeout();
  }, [clearCloseDelayTimeout]);
  const handlePopoverClose = useCallback(() => {
    setInternalOpen(false);
    setIsSticky(false);
    clearCloseDelayTimeout();
    clearOpenDelayTimeout();
  }, [clearCloseDelayTimeout, clearOpenDelayTimeout]);
  const handleClickPopover = useCallback(() => {
    onPopoverLinkClick === null || onPopoverLinkClick === void 0 || onPopoverLinkClick(objectTypeId);
    window.open(url, '_blank', 'noopener,noreferrer');
    handlePopoverClose();
  }, [handlePopoverClose, url, objectTypeId, onPopoverLinkClick]);
  const handleChipClick = useCallback(() => {
    onChipClick === null || onChipClick === void 0 || onChipClick(objectTypeId);
  }, [objectTypeId, onChipClick]);
  const propertiesExcludingDisplayName = useMemo(() => {
    var _HIDE_DISPLAY_NAME_PR;
    const hideDisplayNameProperties = (_HIDE_DISPLAY_NAME_PR = HIDE_DISPLAY_NAME_PROPERTIES[normalizedObjectType]) !== null && _HIDE_DISPLAY_NAME_PR !== void 0 ? _HIDE_DISPLAY_NAME_PR : [];
    const allProperties = [...(primaryProperty ? [primaryProperty] : []), ...secondaryProperties];
    return allProperties.filter(prop => !hideDisplayNameProperties.includes(prop.name));
  }, [normalizedObjectType, primaryProperty, secondaryProperties]);
  const hasProperties = propertiesExcludingDisplayName.length > 0;
  const chipContents = /*#__PURE__*/_jsxs(Flex, {
    display: "inline-flex",
    alignItems: "center",
    gap: "100",
    backgroundColor: "fill-surface-recessed",
    maxInlineSize: "200px",
    borderRadius: "200",
    paddingBlock: "100",
    paddingInline: "100",
    as: "span",
    children: [/*#__PURE__*/_jsx(Flex, {
      as: "span",
      flexShrink: 0,
      children: /*#__PURE__*/_jsx(IconComponent, {
        size: "100",
        color: "fill-brand-default"
      })
    }), /*#__PURE__*/_jsx(Text, {
      as: "span",
      typography: "detail-100",
      children: /*#__PURE__*/_jsx(TruncateString, {
        tooltip: !hasProperties,
        matchContentWidth: true,
        children: resolvedDisplayName
      })
    })]
  });
  const chip = /*#__PURE__*/_jsx(View, {
    "data-test-id": "crm-record-chip",
    display: "inline-block",
    inlineSize: "fit-content",
    maxInlineSize: "100%",
    onMouseEnter: hasProperties ? handleChipMouseEnter : undefined,
    onMouseLeave: hasProperties ? handleChipMouseLeave : undefined,
    children: linkHref ? /*#__PURE__*/_jsx(RemoveTextDecorationForCanvasLinkWrapper, {
      children: /*#__PURE__*/_jsx(Button, {
        "data-crm-record-chip": "",
        "data-skip-nav-link-tracking": true,
        href: linkHref,
        onClick: handleChipClick,
        target: "_top",
        use: "unstyled",
        children: chipContents
      })
    }) : chipContents
  });
  if (!hasProperties) {
    return chip;
  }
  return /*#__PURE__*/_jsx(Popover, {
    open: internalOpen,
    onOpenChange: handlePopoverClose,
    content: /*#__PURE__*/_jsx(View, {
      inlineSize: "360px",
      onMouseEnter: handlePopoverMouseEnter,
      onMouseLeave: handlePopoverMouseLeave,
      onClick: handlePopoverInteraction,
      children: /*#__PURE__*/_jsx(CrmRecordChipCard, {
        title: resolvedDisplayName,
        icon: PopoverIconComponent,
        properties: propertiesExcludingDisplayName,
        onClick: handleClickPopover
      })
    }),
    closeOnOutsideClick: true,
    placement: "right",
    children: chip
  });
};
export default CrmRecordChip;